// frontend/src/pages/ContentCalendarTemplatePage.js
//
// /tools/content-calendar-template
//
// RANKED #2 OF EIGHT on measured evidence (2026-08-16): 2,900/mo at KD 9, CPC
// $14.38, and — unusually for this cluster — **no AI Overview**. A template is a
// file rather than a fact, so Google cannot answer the query in a box the way it
// does for "what size is an Instagram post". That is why a modest-volume term
// outranks 33,100/mo candidates on expected value.
//
// THE SERP, AND WHY WE CAN ENTER IT. canva.com/sheets/social-media-calendar has
// 7 referring domains, notion.com/templates has 0, the roundup at
// socialmediastrategiessummit has 49; only HubSpot's offers page is a real wall
// at 518. Median 49. Our own /blog/instagram-reels-dimensions ranks with zero,
// so the entry cost here is content quality, not links.
//
// THE DIFFERENTIATOR IS THE COLUMN HEADERS. Every competing template is a
// planning grid you fill in and then retype into whatever publishes your posts.
// The generator here emits the exact column set Greatly Social's bulk importer
// accepts, so the finished calendar uploads straight in. No competitor on that
// SERP is a scheduler, so none of them can close that loop.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import ContentCalendarBuilder from '../components/ContentCalendarBuilder'

const URL = '/tools/content-calendar-template'
const HERO = '/assets/images/articles/content-calendar.webp'

const FAQS = [
  ['What is a content calendar?',
   'A schedule of what you are posting, where and when. Its real job is removing the daily decision — you decide once a month what goes out, then execute rather than improvise. The generator above builds one around the days you actually want to post.'],
  ['What should a content calendar include?',
   'At minimum: the date, the time, the network and the post itself. Beyond that, a per-network caption, the media file, alt text and a first comment cover almost everything you will need. Those are exactly the columns the template uses.'],
  ['How far ahead should I plan?',
   'A month is the sweet spot for most people. A week is not enough to batch properly, and a quarter tends to go stale — half the posts stop making sense by week eight.'],
  ['How often should I post?',
   'Three to five times a week that you can sustain beats seven for a fortnight and then nothing. Pick the days in the tool above and keep them.'],
  ['Is the template free?',
   'Yes — no sign-up, no email required. It is built in your browser and downloads as a CSV.'],
  ['Can I upload it straight into Greatly Social?',
   'Yes, and that is the point of it. The columns match the bulk importer, so once you have filled in the captions you can upload the file and the whole month is scheduled.'],
]

const SECTIONS = [
  ['Why most content calendars get abandoned',
   'Not because people stop caring — because a blank grid asks you to invent something in the exact moment you are least able to. A calendar that arrives pre-filled with dates, times and prompts asks a much smaller question: what goes in this slot, rather than what should I do about social media. That is why the generator fills each slot with a content prompt by default.'],
  ['Plan the slots before the content',
   'Decide the days and times first, then fill them. Doing it the other way round — writing posts and then finding homes for them — is how calendars end up front-loaded, with eleven posts in week one and nothing after. Fixed slots also make gaps visible, which is the whole reason a calendar beats a list.'],
  ['A month, not a quarter',
   'Quarterly plans look impressive and go stale. Products change, something happens in your industry, a post you wrote in January reads oddly by March. A month is long enough to batch efficiently and short enough that everything in it still makes sense when it publishes.'],
  ['Rotate content pillars rather than reinventing',
   'Most sustainable accounts cycle a handful of recurring types — teach something, show the work, prove a result, make an offer, tell the story, ask a question. Rotating pillars removes the blank-page problem without making everything look the same. The prompts in the generator rotate through exactly those six.'],
]

export default function ContentCalendarTemplatePage () {
  return (
    <>
      <Seo
        title='Content Calendar — Free Template Builder'
        description='Free content calendar template. Pick your posting days and download a filled-in CSV for the month — it uploads straight into a scheduler.'
        path={URL}
        image={HERO}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: FAQS.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: 'Content calendar template builder',
            url: `${SITE_URL}${URL}`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>Content calendar template builder</h1>
              <p className='lead mb-0'>
                Pick your posting days and download a filled-in calendar for the month — as a CSV
                that uploads straight into a scheduler, instead of a grid you have to retype.
              </p>
            </Col>
            <Col lg={5}>
              <Image src={HERO} alt='A month planned out on a desk calendar' fluid rounded
                     loading='eager' width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <ContentCalendarBuilder />
        </Container>
      </section>

      {/* ───────── THE ANSWER ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            <strong>A content calendar is a schedule of what you are posting, where and when.</strong>{' '}
            Its real value is not organisation — it is removing the daily decision. You choose once
            a month what goes out, and after that you are executing rather than improvising in front
            of an empty box.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Which is why the format matters less than whether you can actually keep using it. Most
            templates are a spreadsheet you fill in and then retype somewhere else. This one is the
            file your scheduler already accepts.
          </p>
        </Container>
      </section>

      {/* ───────── WHAT'S IN IT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>What is in the template</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr><th>Column</th><th>What it holds</th></tr>
              </thead>
              <tbody>
                {[['date', 'The day it publishes'],
                  ['time', 'The time it publishes'],
                  ['timezone', 'So a scheduled 9:30am means yours, not the server\'s'],
                  ['caption', 'The post itself — pre-filled with a content prompt if you want one'],
                  ['media', 'A filename or URL for the image or video'],
                  ['networks', 'Which platforms this row publishes to'],
                  ['caption_instagram / _linkedin / _x', 'Override the caption for a specific network'],
                  ['first_comment', 'Hashtags or a follow-up, posted as the first comment'],
                  ['alt_text', 'Accessibility, and it helps image search'],
                  ['pinterest_board / youtube_title', 'The two fields those platforms need and others do not']].map(([c, d]) => (
                  <tr key={c}>
                    <td className='fw-semibold'><code>{c}</code></td>
                    <td className='text-muted'>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            These are the columns Greatly Social's bulk importer reads, which is why the finished
            file can be uploaded rather than copied out by hand.
          </p>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {SECTIONS.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly Social
                you can upload the file you just built and have the whole month scheduled across
                eight networks — Instagram, TikTok, Facebook, LinkedIn, YouTube, Pinterest, Threads
                and X — then see it all on one drag-and-drop calendar.
              </p>
              <p className='text-muted'>
                Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {[['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools/instagram-post-size', 'Image resizer', 'Every Instagram size'],
              ['/tools/instagram-bio-generator', 'Bio generator', 'Fits the character limit'],
              ['/best-time-to-post', 'Best time to post', 'A schedule for your audience'],
              ['/tools/engagement-rate-calculator', 'Engagement rate calculator', 'Per-platform formulas'],
              ['/calendar-view', 'Content calendar', 'The product version of this']].map(([href, t, d]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{t}</div>
                  <div className='text-muted small'>{d}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>A spreadsheet is where most calendars stop</h2>
          <p className='text-muted fs-5'>
            At Greatly Social the file you just built is the input, not the end. Upload it and the
            month is scheduled.
          </p>
          <Row className='g-3 mb-4'>
            {[['Upload the whole month', 'One CSV, a month of posts queued.'],
              ['Eight networks, one row', 'A separate caption per network on the same post.'],
              ['Drag to reschedule', 'Move a post and its time goes with it.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/bulk-posting'>See bulk upload</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can upload the month and be done</h2>
          <p className='lead mb-4'>
            Fill in the captions, upload the file, and the whole calendar publishes itself. Free
            for 14 days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
